import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { FilterBanner } from '~/components/ui/Form';
import { PageTitle } from '~/components/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { Table } from '~/components/ui/Table';
import { useStudentFocus } from '~/modules/studentFocus';

type StudyHelperInformation = components['schemas']['StudyHelperInformation'];

type StudyHelpersSearch = {
  group_id?: number;
};

export const Route = createFileRoute('/_admin/study-helpers/')({
  component: StudyHelpersList,
  validateSearch: (search: Record<string, unknown>): StudyHelpersSearch => {
    const raw = search.group_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      group_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<StudyHelperInformation>();

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor((row) => row.display_name, {
    id: 'name',
    header: 'Name',
  }),
  columnHelper.accessor((row) => row.email, { id: 'email', header: 'Email' }),
  columnHelper.accessor((row) => row.country, {
    id: 'country',
    header: 'Country',
  }),
  columnHelper.accessor((row) => row.congregation_name, {
    id: 'congregation',
    header: 'Congregation',
  }),
];

function StudyHelpersList() {
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const { data, isLoading, error } = api.getStudyHelpers.useQuery({
    query: {
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
      ...(search.group_id !== undefined ? { group_id: search.group_id } : {}),
      // Respect the app-wide student focus: scope to that student's helper.
      ...(focusedStudent ? { student_id: focusedStudent.id } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <PageTitle>Study Helpers</PageTitle>

      {search.group_id !== undefined ? (
        <FilterBanner>
          <span>
            Filtered to members of group{' '}
            <Link
              to="/groups/$groupId"
              params={{ groupId: String(search.group_id) }}
            >
              #{search.group_id}
            </Link>
          </span>
          <Link to="/study-helpers" search={{}}>
            Clear filter
          </Link>
        </FilterBanner>
      ) : null}

      {isLoading ? (
        <LoadingMessage>Loading Study Helpers...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>Error loading Study Helpers</ErrorMessage>
      ) : (
        <>
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
          />

          <Table
            table={table}
            to="/study-helpers/$studyHelperId"
            getParams={(row) => ({ studyHelperId: String(row.id) })}
          />
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
            hideWhenAlone
          />
        </>
      )}
    </div>
  );
}

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
